import { createRouter } from '@tanstack/react-router'
import qs from 'qs'

import { BrandLoading, ErrorPage } from './components/common'
import { QS_CONFIG } from './constants/api'
import { indexRoute } from './routes'
import { aboutAppRoute } from './routes/about-app'
import { affiliate1076Route } from './routes/affiliate-1076'
import { affiliateAdsRoute } from './routes/affiliate-ads'
import { affiliateHomeRoute } from './routes/affiliate-home'
import { affiliateSmsRoute } from './routes/affiliate-sms'
import { announcementsRoute } from './routes/announcements'
import { bingoRegisterRoute } from './routes/bingo-register'
import { blogRoute } from './routes/blogs'
import { blogDetailsRoute } from './routes/blogs/blog-details'
import { blogsListRoute } from './routes/blogs/blog-list'
import { btiSportsRoute } from './routes/bti-sports'
import { casinoRoute } from './routes/casino'
import { casinoIndexRoute } from './routes/casino/casino'
import { casinoCategoryRoute } from './routes/casino/category'
import { gameRoute } from './routes/casino/game'
import { casinoAdsRoute } from './routes/casino-ads'
import { casinoRegisterRoute } from './routes/casino-register'
import { chRegisterRoute } from './routes/ch-register'
import { contactUsRoute } from './routes/contact-us'
import { fallbackMxRoute } from './routes/fallback-mx'
import { footerPageRoute } from './routes/footer-page'
import { googleAdsRegisterRoute } from './routes/ga-register'
import { gameBackRoute } from './routes/game-back'
import { gameDepositRoute } from './routes/game-deposit'
import { helpPageRoute } from './routes/help-page'
import { loginRoute } from './routes/login'
import { lpRegisterRoute } from './routes/lp-register'
import { newsRoute } from './routes/news'
import { newsDetailsRoute } from './routes/news/news-details'
import { newsListRoute } from './routes/news/news-list'
import { notFoundRoute } from './routes/not-found'
import { pokerRegisterRoute } from './routes/poker-register'
import { profileRoute } from './routes/profile'
import { affiliateRoute } from './routes/profile/affiliate'
import { affiliateIndexRoute } from './routes/profile/affiliate/affiliate'
import { affiliatePolicyRoute } from './routes/profile/affiliate/affiliate-policy'
import { memberListRoute } from './routes/profile/affiliate/member-list'
import { appFeedbackRoute } from './routes/profile/app-feedback'
import { commissionRoute } from './routes/profile/commission'
import { financialTransactionRoute } from './routes/profile/financial-transaction'
import { freeBonusRoute } from './routes/profile/free-bonus'
import { freeSpinsRoute } from './routes/profile/free-spins'
import { inboxRoute } from './routes/profile/inbox'
import { kycRoute } from './routes/profile/kyc'
import { profileIndexRoute } from './routes/profile/profile'
import { redeemBonusRoute } from './routes/profile/redeem-bonus'
import { redeemBonusDetailsRoute } from './routes/profile/redeem-bonus-details'
import { referralRoute } from './routes/profile/referral'
import { invitationCodesRoute } from './routes/profile/referral/invitation-codes'
import { myInvitationRoute } from './routes/profile/referral/my-invitation'
import { referralIndexRoute } from './routes/profile/referral/referral'
import { referralPolicyRoute } from './routes/profile/referral/referral-policy'
import { transactionHistoryRoute } from './routes/profile/transaction-history'
import { transactionLimitRoute } from './routes/profile/transaction-limit'
import { wageringTransactionRoute } from './routes/profile/wagering-transaction'
import { promotionsRoute } from './routes/promotions'
import { promoTermsRoute } from './routes/promotions/promo-terms'
import { promotionsIndexRoute } from './routes/promotions/promotions'
import { registerRoute } from './routes/register'
import { rootRoute } from './routes/root'
import { ruletaRegisterRoute } from './routes/ruleta-register'
import { slotRegisterRoute } from './routes/slot-register'
import { sponsorsRoute } from './routes/sponsors'
import { sponsorsDetailsRoute } from './routes/sponsors/sponsors-details'
import { sponsorsListRoute } from './routes/sponsors/sponsors-list'
import { sportAdsRoute } from './routes/sport-landing-page'
import { sportsRegister } from './routes/sports-register'
import { tjRegisterRoute } from './routes/tj-register'
import { vipCenterRoute } from './routes/vip-center'

const routeTree = rootRoute.addChildren([
  indexRoute,
  casinoRoute.addChildren([casinoIndexRoute, casinoCategoryRoute, gameRoute]),
  affiliateHomeRoute,
  announcementsRoute,
  blogRoute.addChildren([blogsListRoute, blogDetailsRoute]),
  newsRoute.addChildren([newsListRoute, newsDetailsRoute]),
  sponsorsRoute.addChildren([sponsorsListRoute, sponsorsDetailsRoute]),
  contactUsRoute,
  helpPageRoute,
  footerPageRoute,
  aboutAppRoute,
  profileRoute.addChildren([
    profileIndexRoute,
    affiliateRoute.addChildren([
      affiliateIndexRoute,
      affiliatePolicyRoute,
      memberListRoute,
    ]),
    commissionRoute,
    financialTransactionRoute,
    freeBonusRoute,
    freeSpinsRoute,
    inboxRoute,
    kycRoute,
    referralRoute.addChildren([
      referralIndexRoute,
      invitationCodesRoute,
      myInvitationRoute,
      referralPolicyRoute,
    ]),
    transactionHistoryRoute,
    appFeedbackRoute,
    wageringTransactionRoute,
    transactionLimitRoute,
    redeemBonusRoute,
    redeemBonusDetailsRoute,
  ]),
  promotionsRoute.addChildren([promotionsIndexRoute, promoTermsRoute]),
  registerRoute,
  btiSportsRoute,
  sportsRegister,
  casinoAdsRoute,
  affiliateAdsRoute,
  bingoRegisterRoute,
  casinoRegisterRoute,
  chRegisterRoute,
  pokerRegisterRoute,
  ruletaRegisterRoute,
  slotRegisterRoute,
  tjRegisterRoute,
  lpRegisterRoute,
  googleAdsRegisterRoute,
  sportAdsRoute,
  vipCenterRoute,
  loginRoute,
  affiliateSmsRoute,
  affiliate1076Route,
  gameDepositRoute,
  gameBackRoute,
  fallbackMxRoute,
  notFoundRoute, // Should be always last
])

export function customStringifier(searchObj: unknown) {
  return encodeURI(
    qs.stringify(searchObj, { ...QS_CONFIG, addQueryPrefix: true }),
  )
}

function customParser(searchString: string) {
  return qs.parse(searchString, { ...QS_CONFIG, ignoreQueryPrefix: true })
}

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Suspense fallback while a lazy route chunk (and its on-demand i18n
  // namespaces) loads. Without it the router falls back to null and the app
  // flashes a blank white page between the splash and the route's first paint.
  defaultPendingComponent: () => <BrandLoading fullScreen />,
  defaultErrorComponent: ({ error }) => (
    <ErrorPage errorMessage={error.message} />
  ),
  stringifySearch: customStringifier,
  parseSearch: customParser,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
